import AddressHistoryModal from "components/AddressHistory/AddressHistoryModal";
import CustomerAddressSelect from "components/Customer/CustomerAddressSelect";
import ShipAddressValidator from "components/ShipAddressValidator/ShipAddressValidator";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function ShippingAddress({
  selectedWorkOrder,
  refreshFunc,
  disableCustomerSelect,
  // An address the W-Details form is carrying but has not saved yet (set when
  // the shipment method is switched to or away from pickup). Displayed in
  // place of the stored one so the card is never contradicting the form.
  pendingShippingAddress = null,
}) {
  const dispatch = useDispatch();

  /*   All States
   ********************************************* */
  const [shippingAddress, setShippingAddress] = useState(null);
  // True while the address history modal is open.
  const [showAddressHistory, setShowAddressHistory] = useState(false);
  // How many times Nova Shipping has rewritten this address. Drives whether
  // the icon appears at all.
  const [addressHistoryCount, setAddressHistoryCount] = useState(0);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressSelectionMode, setAddressSelectionMode] = useState("edit");

  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);

  /*   All Functions
   ********************************************* */

  const getBillingAsShippingData = () => {
    let billingAddress =
      typeof selectedWorkOrder?.billingAddress === "string"
        ? JSON.parse(selectedWorkOrder?.billingAddress)
        : selectedWorkOrder?.billingAddress;

    return {
      addressCompany: billingAddress?.addressCompany,
      addressLine1: billingAddress?.addressLine1,
      addressLine2: billingAddress?.addressLine2,
      city: billingAddress?.city,
      state: billingAddress?.state,
      zip: billingAddress?.zip,
      country: billingAddress?.country,
    };
  };

  // In-modal "Copy" button — just fills the open form for the user to
  // review/edit before clicking Update.
  const handleCopy = () => {
    setFormData(getBillingAsShippingData());
  };

  // Card-header "Copy" button — copies billing to shipping and saves
  // immediately, no modal/review step.
  const handleCopyAndSave = async () => {
    const isUpdated = await updateShippingAddress(getBillingAsShippingData());
    if (isUpdated) {
      await refreshFunc();
      dispatch(
        setNotification({
          message: "Billing address copied to shipping",
          type: "success",
        }),
      );
    }
  };

  const getCustomerById = useCallback(
    async (id) => {
      try {
        loadingOn();
        const { data } = await API.get(`/customers/${id}`, {
          headers: authHeader(),
        });

        if (data?.success) {
          setSelectedCustomer(data?.data);
        }
        loadingOff();
      } catch (err) {
        loadingOff();
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          }),
        );
      }
    },
    [dispatch],
  );

  const loadingOn = () => {
    setLoading(true);
  };
  const loadingOff = () => {
    setLoading(false);
  };

  const createShippingAddressPayload = (address, checkMarks = {}) => {
    return {
      addressCompany: address?.companyName || address?.addressCompany || "",
      addressLine1: address?.addressLine1 || "",
      addressLine2: address?.addressLine2 || "",
      city: address?.city || "",
      state: address?.state || "",
      zip: address?.zip || "",
      country: address?.country || "",
      signatureRelease: address?.signatureRelease || false,
      authorizePerson: address?.authorizePerson || "",
      authorizeLocation: address?.authorizeLocation || "",
      selfCollect: address?.selfCollect || false,
      serviceId: address?.serviceId || "",
      addressType: address?.addressType || "",
      addressDhlTransit: address?.addressDhlTransitDays || null,
      addressGroundTransitDays: address?.addressGroundTransitDays || null,
      ...checkMarks,
    };
  };

  const updateShippingAddress = async (address, checkMarks = {}) => {
    try {
      loadingOn();

      const _shippingAddress = createShippingAddressPayload(
        address,
        checkMarks,
      );
      const apiData = {
        id: selectedWorkOrder?.id,
        data: { shippingAddress: _shippingAddress },
      };

      const { data } = await API.patch("/work-orders", apiData, {
        headers: authHeader(),
      });

      if (data?.message?.type === "success") {
        return true;
      }

      console.log(data);
      alert("something went wrong check logs");
      return false;
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      return false;
    } finally {
      loadingOff();
    }
  };

  const handleSelectAddress = async (address) => {
    // {
    //     id: 1,
    //     addressId: 1,
    //     companyId: 3,
    //     customerId: 14,
    //     sortId: 1,
    //     companyName: 'Mr Code Crafter',
    //     addressLine1: 'Add 1',
    //     addressLine2: 'add 23',
    //     city: 'sialkot',
    //     state: 'GA',
    //     zip: '51310',
    //     country: 'US',
    //     addressType: '',
    //     addressGroundTransitDays: 0,
    //     authorizePerson: '',
    //     authorizeLocation: '',
    //     serviceId: '',
    //     addressDhlTransitDays: 0,
    //     signatureRelease: false,
    //     selfCollect: false,
    //     isDefault: true,
    //     createdAt: '2025-10-15T05:27:43.000Z',
    //     updatedAt: '2025-10-15T05:56:17.000Z'
    //   }

    let shippingAddressData = {
      addressCompany: address?.companyName || address?.addressCompany || "",
      addressLine1: address?.addressLine1,
      addressLine2: address?.addressLine2,
      city: address?.city,
      state: address?.state,
      zip: address?.zip,
      country: address?.country,
      signatureRelease: address?.signatureRelease || false,
      authorizePerson: address?.authorizePerson || "",
      authorizeLocation: address?.authorizeLocation || "",
      selfCollect: address?.selfCollect || false,
      serviceId: address?.serviceId || "",
      addressType: address?.addressType || "",
      addressDhlTransitDays: address?.addressDhlTransitDays || null,
      addressGroundTransitDays: address?.addressGroundTransitDays || null,
    };

    if (addressSelectionMode === "quick") {
      const isUpdated = await updateShippingAddress(shippingAddressData);
      if (isUpdated) {
        await refreshFunc();
      }
      setShowAddressModal(false);
      return;
    }

    setFormData(shippingAddressData);
    setShowAddressModal(false);
  };

  const handleUpdateShippingAddress = async (e, address, checkMarks) => {
    e.preventDefault();
    console.log(address);

    // {
    //   addressId: address.addressId,
    //   id: address.id,
    //   companyName: address.companyName || "",
    //   addressLine1: address.addressLine1 || "",
    //   addressLine2: address.addressLine2 || "",
    //   city: address.city || "",
    //   state: address.state || "",
    //   zip: address.zip || "",
    //   country: address.country || "",
    //   addressType: address.addressType || "",
    //   addressGroundTransitDays: address.addressGroundTransitDays || null,
    //   authorizePerson: address.authorizePerson || "",
    //   authorizeLocation: address.authorizeLocation || "",
    //   serviceId: address.serviceId || "",
    //   addressDhlTransitDays: address.addressDhlTransitDays || null,
    //   signatureRelease: checkMarks?.signatureRelease || false,
    //   selfCollect: checkMarks?.selfCollect || false,
    // };

    const isUpdated = await updateShippingAddress(address, checkMarks);
    if (isUpdated) {
      await refreshFunc();
      setShowUpdateModal(false);
      setFormData(null);
    }
  };

  const getParsedShippingAddress = (address) => {
    if (!address) return null;

    let parsedAddress = address;

    // Keep parsing until we get an object or null
    while (typeof parsedAddress === "string") {
      try {
        parsedAddress = JSON.parse(parsedAddress);
      } catch (error) {
        console.error("Failed to parse shipping address:", error);
        return null;
      }
    }

    return parsedAddress;
  };

  /**
   * The history COUNT, fetched rather than read off the work order.
   *
   * selectedWorkOrder arrives from two different endpoints depending on how
   * the page was reached -- /work-order/:id returns every column, but the
   * list fetch used when navigating within a filtered set does not, so
   * addressHistoryLog is present on one path and missing on the other. Gating
   * the icon on the row therefore hid it exactly half the time.
   *
   * Cheap: one small row, and only the count is used here. The entries
   * themselves are still fetched by the modal, when someone opens it.
   *
   * Failure is silent on purpose -- an address card must not surface an error
   * because an optional history lookup did not answer.
   */
  useEffect(() => {
    const woId = selectedWorkOrder?.woId;
    if (!woId) {
      setAddressHistoryCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.post(
          "/bundle-sync/address-history",
          { woId },
          { headers: authHeader() },
        );
        if (!cancelled && data?.success) {
          setAddressHistoryCount((data.data?.history || []).length);
        }
      } catch {
        if (!cancelled) setAddressHistoryCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedWorkOrder?.woId]);

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    // The unsaved one wins while it exists: after Save the parent refreshes
    // the work order and clears it, and this falls back to the stored value.
    let shippingAddress = getParsedShippingAddress(
      pendingShippingAddress || selectedWorkOrder?.shippingAddress,
    );

    setShippingAddress(shippingAddress);
    // setShippingAddress(shippingAddress);
    if (showUpdateModal) {
      setFormData(shippingAddress);
    }
  }, [selectedWorkOrder, showUpdateModal, pendingShippingAddress]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (selectedWorkOrder?.customer && showUpdateModal) {
        getCustomerById(selectedWorkOrder?.customer?.customerId);
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedWorkOrder, showUpdateModal, getCustomerById]);

  return (
    <div className="bg-white shadow-lg w-50 px-3 pb-2 custom-border-radius-sm">
      {loading && <Spinner />}
      {/*   WO Info
       ********************************************* */}
      <div className="d-flex inline-block justify-content-between align-items-center">
        <h4 className="mb-0">Shipping Address </h4>
        <h4 className="mb-0">
          <div className="d-flex gap-1 align-items-center">
            {/* Shipping address history, when Nova Shipping has rewritten this
                address. Hidden at zero -- on most orders it never has been,
                and an icon there would open an empty box.

                Same icon and same modal as the order row in Nova Shipping's
                invoice Details: it answers the same question in the other
                place someone asks it. */}
            {addressHistoryCount > 0 && (
              <button
                type="button"
                className="btn btn-link p-0 text-muted lh-1 me-1"
                onClick={() => setShowAddressHistory(true)}
                title={`Shipping address changed ${addressHistoryCount} time${
                  addressHistoryCount === 1 ? "" : "s"
                } — see what it was`}
                aria-label="Shipping address history"
              >
                <i className="mdi mdi-history fs-4" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="btn btn-info btn-sm"
              onClick={() => setShowUpdateModal(true)}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setAddressSelectionMode("quick");
                setShowAddressModal(true);
              }}
              disabled={!disableCustomerSelect}
            >
              Choose
            </button>
            <button
              onClick={handleCopyAndSave}
              type="button"
              className="btn btn-sm rounded border-success"
              title="Copy billing address to shipping"
            >
              <i className="mdi mdi-arrow-left"></i> Copy
            </button>
          </div>
        </h4>
      </div>
      <hr className="mt-0 mb-1 w-50" />
      {/* Job Name */}
      {/* <div className="row">
        <div className="col-4 fw-bold"> Description</div>
        <div className="col-8">{shippingAddress?.description}</div>
      </div> */}
      <div className="row">
        <div className="col-4 fw-bold">Customer</div>
        <div className="col-8">{shippingAddress?.addressCompany}</div>
      </div>
      <div className="row">
        <div className="col-4 fw-bold">Address Line 1</div>
        <div className="col-8">{shippingAddress?.addressLine1}</div>
      </div>
      <div className="row">
        <div className="col-4 fw-bold">Address Line 2</div>
        <div className="col-8">{shippingAddress?.addressLine2}</div>
      </div>
      <div className="row">
        <div className="col-4 fw-bold">City | State | Zip</div>
        <div className="col-8">
          {shippingAddress?.city} | {shippingAddress?.state} |{" "}
          {shippingAddress?.zip}
        </div>
      </div>
      <div className="row">
        <div className="col-4 fw-bold">Country</div>
        <div className="col-8">{shippingAddress?.country}</div>
      </div>
      <div className="row">
        <div className="col-4 fw-bold">Transit Days</div>
        <div className="col-8">
          {shippingAddress?.addressGroundTransitDays || (
            <span className="text-danger">Please Validate Address</span>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-4 fw-bold">Signature Release</div>
        <div className="col-8">
          {shippingAddress?.signatureRelease ? "Yes" : "No"}
        </div>
      </div>
      {shippingAddress?.signatureRelease && (
        <>
          <div className="row">
            <div className="col-4 fw-bold">Authorize Person</div>
            <div className="col-8">{shippingAddress?.authorizePerson}</div>
          </div>
          <div className="row">
            <div className="col-4 fw-bold">Authorize Location</div>
            <div className="col-8">{shippingAddress?.authorizeLocation}</div>
          </div>
        </>
      )}
      <div className="row">
        <div className="col-4 fw-bold">Self Collect</div>
        <div className="col-8">
          {shippingAddress?.selfCollect ? "Yes" : "No"}
        </div>
      </div>

      <div className="row">
        <div className="col-4 fw-bold">Service ID</div>
        <div className="col-8">{shippingAddress?.serviceId}</div>
      </div>
      {/* <div className="row">
        <div className="col-4 fw-bold">Drop Shipping</div>
        <div className="col-8">
          {shippingAddress?.dropShipping ? "Yes" : "No"}
        </div>
      </div> */}

      {/*   Customer Select Modal
       ********************************************* */}
      <Modal
        size="xl"
        contentClassName="bg-transparent"
        show={showUpdateModal}
        onHide={() => {
          setFormData(null);
          setShowUpdateModal(false);
        }}
      >
        <Modal.Body>
          <div
            className="bg-white shadow-lg px-3 pb-2 pt-2 mb-2 me-2"
            style={{ borderRadius: 20 }}
          >
            <div className="fw-bold fs-4 mt-3 d-flex justify-content-between align-items-center">
              <div>Shipping Address</div>
              <div className="d-flex gap-2">
                <button
                  onClick={handleCopy}
                  type="button"
                  className="btn btn-sm rounded border-success mb-1"
                >
                  <i className="mdi mdi-arrow-left"></i> Copy
                </button>
                <button
                  onClick={() => {
                    setAddressSelectionMode("edit");
                    setShowAddressModal(true);
                  }}
                  disabled={!disableCustomerSelect}
                  type="button"
                  className="btn btn-sm btn-primary mb-1"
                >
                  Choose
                </button>
              </div>
            </div>
            <hr className="my-0 py-0 mb-2" />
            {selectedCustomer && (
              <ShipAddressValidator
                isAddMode={false}
                shippingAddress={formData}
                isWorkOrder={true}
                showModal={setShowUpdateModal}
                submitFunction={handleUpdateShippingAddress}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/*   Customer Address Modal
       ********************************************* */}
      <Modal
        size="xl"
        contentClassName="bg-transparent"
        show={showAddressModal}
        onHide={() => setShowAddressModal(false)}
      >
        <Modal.Body>
          <CustomerAddressSelect
            customerId={selectedWorkOrder?.customerId}
            isPrimaryId={true}
            handleSelectAddress={handleSelectAddress}
          />
        </Modal.Body>
      </Modal>

      {showAddressHistory && (
        <AddressHistoryModal
          woId={selectedWorkOrder?.woId}
          onHide={() => setShowAddressHistory(false)}
        />
      )}
    </div>
  );
}
